/* eslint-disable react/forbid-prop-types */
import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Card } from './styles';

import NoData from '../../../../../components/NoData';
import MyModal from '../../../../../components/Modal';
import download from '../../../../../assets/images/icons/download.svg';
import { useAppContext } from '../../../../../contexts/auth';
import DashboardService from '../../../../../services/affreightment/DashboardService';
import formatDate from '../../../../../utils/formatDate';
import ExportXlsx from '../../../../../utils/ExportXlsx';
import { AffreightmentRelResponse, UserCostType } from '../../types';
import useApiCall from '../../../../../hooks/useApiCall';

interface UserCostCardInterface {
  dashboardUserCostData: UserCostType;
  selectedWorkplace: { value: number, label: string};
}

export default function UserCostCard({ dashboardUserCostData, selectedWorkplace }: UserCostCardInterface) {
  const today = new Date();
  today.setHours(today.getHours() - 3);
  const todayFormatted = today.toJSON().slice(0, 10);

  const [selectedDate, setSelectedDate] = useState(todayFormatted);
  const [selectDateModalShow, setSelectDateModalShow] = useState(false);

  const { userData, selectedCompany } = useAppContext();
  const { userCode, userProfile } = userData;
  const companyCode = selectedCompany.value;

  const { apiCall } = useApiCall();


  const downloadRel = useCallback(async () => {
    await toast.promise(apiCall({
      apiToCall: DashboardService.getRelsData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        codViagem: 0,
        codRelatorio: 3,
        dataViagens: selectedDate,
        mes: todayFormatted.split('-')[1],
        ano: todayFormatted.split('-')[0],
        dataPesquisa: todayFormatted,
      }),
      actionAfterResponse: (response: AffreightmentRelResponse) => {
        if (response.custoUsuario) {
          const custoUsuarioRelData = response.custoUsuario.map((data) => ({
            'Local | Turno': data.localDescr,
            Linha: data.linha,
            Capacidade: data.capacidade,
            Ocupação: data.ocupacao,
            'Valor mensal': data.valorMesLin.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Custo ideal (com ocupação máxima)': data.custoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Custo real (com ocupação atual)': data.custoEfetivo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),

            'Porcentagem de ocupação': `${data.percOcupacao} %`,
          }));
          ExportXlsx({
            data: custoUsuarioRelData,
            filename: `Relatório custo usuário ${formatDate(selectedDate)}`,
          });
        }

        if (!response.custoUsuario) {
          toast.error('Não há dados para serem baixados');
        }
      },
      catchMessage: 'Não foi possível baixar o relatório de usuários de VT',
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }, [apiCall, userCode, userProfile, companyCode, selectedDate, todayFormatted]);

  function handleDateChange(event: ChangeEvent<HTMLInputElement>) {
    setSelectedDate(event.target.value);
  }

  return (
    <>
      <Card>
        <div className="card-title">
          <div>Custo/usuário</div>
          <img
            src={download}
            alt="download"
            title="Baixar relatório excel"
            onClick={() => setSelectDateModalShow(true)}
          />
        </div>

        {dashboardUserCostData && (
          <>
            <div className="card-main">
              <div>
              R$
                {' '}
                {dashboardUserCostData.custoFunc}
              </div>
              <small>
              Por usuário
              </small>
            </div>

            <footer>
              {(!dashboardUserCostData.custoBaixou) && (
                <>
                  <div className="porcentagem-negativa">
                +
                    {' '}
                    {dashboardUserCostData.percCusto}
                %
                  </div>
                  <div>
                Em relação ao início da operação
                  </div>
                </>
              )}
              {(dashboardUserCostData.custoBaixou) && (
                <>
                  <div className="porcentagem-positiva">
                -
                    {' '}
                    {dashboardUserCostData.percCusto}
                %
                  </div>
                  <div>
                Em relação ao início da operação
                  </div>
                </>
              )}
            </footer>
          </>
        )}

        {(!dashboardUserCostData) && (
          <NoData
            icon="emptyBox"
            label={(
              <>
              O local de trabalho
                {' '}
                <strong>{selectedWorkplace.label}</strong>
                {' '}
              não possui dados de custo/usuário.
              </>
            )}
          />
        )}
      </Card>

      <MyModal
        show={selectDateModalShow}
        title="Escolha uma data"
        modalBody={(
          <input type="date" value={selectedDate} onChange={handleDateChange} />
        )}
        closeButtonLabel="Fechar"
        actionButtonLabel="Baixar relatório"
        onClose={() => setSelectDateModalShow(false)}
        onAction={() => {
          downloadRel();
          setSelectDateModalShow(false);
        }}
        type="action"
        size="sm"
      />
    </>
  );
}
